import React, { useCallback, useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
} from '@mui/material';
import api from '../../services/api';

type SiteRow = {
  anonymous_site_id: string;
  events_30d: number;
  false_positive_rate: number;
  events_per_day_avg: number;
  predicted_busy_hours: number[];
};

const AnonymousBenchmarkWidget: React.FC = () => {
  const [rows, setRows] = useState<SiteRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get<{ sites: SiteRow[] }>('/api/analytics/benchmark-anonymous', {
        days: 30,
      });
      setRows(data.sites || []);
      setError(null);
    } catch {
      setError('벤치마크 데이터를 불러오기 실패');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        익명 시설 벤치마크 (P3-01)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        시설명·코드는 포함되지 않음. 이벤트 수·오탐률·혼잡 예상 시각만 비교.
      </Typography>
      {loading && <CircularProgress size={28} />}
      {error && <Alert severity="warning">{error}</Alert>}
      {!loading && !error && (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>익명 ID</TableCell>
                <TableCell align="right">이벤트(30일)</TableCell>
                <TableCell align="right">일평균</TableCell>
                <TableCell align="right">오탐률</TableCell>
                <TableCell>혼잡 예상(시)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.anonymous_site_id}>
                  <TableCell>{r.anonymous_site_id}</TableCell>
                  <TableCell align="right">{r.events_30d}</TableCell>
                  <TableCell align="right">{r.events_per_day_avg}</TableCell>
                  <TableCell align="right">
                    {(r.false_positive_rate * 100).toFixed(1)}%
                  </TableCell>
                  <TableCell>
                    {(r.predicted_busy_hours || []).map((h) => `${h}시`).join(', ') || '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default AnonymousBenchmarkWidget;
